import { useApps } from '@auth/useApps'
import SupportButton from '@components/button/SupportButton'
import theme, { colors } from '@globals/themes/defaultTheme'
import { tabletQuery } from '@globals/themes/themeConstants'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Drawer from '@mui/material/Drawer'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import useMediaQuery from '@mui/material/useMediaQuery'
import { drawerPaperStyles, ModalProps } from '@shared/dialog/BottomDrawer'
import { AppPermission } from '@shared/permissions'
import { t } from '@transifex/native'
import { T } from '@transifex/react'
import { ReactNode } from 'react'

const cardActionStyle = {
  pb: 0,
  '& .MuiCardHeader-avatar': { mr: 1 },
  '& .MuiCardHeader-action': { mt: 0.5, mr: 1 },
}

type OnboardingGuideDialogProps = {
  open: boolean
  onClose: () => void
  helpUrl?: string
  content: ReactNode
  onBack?: () => void
  onDismissGuide?: () => void
  anchorEl?: HTMLElement | null
  routeElementId?: string
}

const OnboardingGuideDialog: React.FC<OnboardingGuideDialogProps> = ({
  open,
  onClose,
  helpUrl,
  content,
  onBack,
  onDismissGuide,
  anchorEl,
  routeElementId,
}) => {
  const isTablet = useMediaQuery(tabletQuery)
  const [hasAppPermission] = useApps()
  const showBackButton = hasAppPermission('onboardingApp', AppPermission.OnboardingAppManage)

  const BackButton = () =>
    showBackButton && (
      <Button
        id="btnBack"
        onClick={onBack}
        size="small"
        disabled={!onBack}
        aria-label={t('Go back')}>
        <ArrowBackIcon />
      </Button>
    )

  const DismissButton = () => (
    <Button
      id="btnDismissGuide"
      onClick={() => onDismissGuide?.()}
      variant="text"
      disabled={!onDismissGuide}>
      <T _str="Dismiss guide" />
    </Button>
  )

  if (isTablet) {
    return (
      <Drawer
        anchor="bottom"
        variant="temporary"
        open={open}
        onClose={onClose}
        sx={drawerPaperStyles}
        ModalProps={ModalProps}>
        <Box p={2} pb={2} sx={{ backgroundColor: colors.whiteDark }}>
          {!!onBack && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <BackButton />
              <DismissButton />
            </Stack>
          )}
          {content}
          {!!helpUrl && <SupportButton helpUrl={helpUrl} routeElementId={routeElementId} />}
        </Box>
      </Drawer>
    )
  }

  // Use a virtual anchor with a small gap so the popover does not cover the FAB
  const GAP_PX = 16
  const rect = anchorEl?.getBoundingClientRect()
  const anchorPosition = rect
    ? { top: rect.top + window.scrollY - GAP_PX, left: rect.right + window.scrollX }
    : undefined
  const useAnchorPosition = Boolean(anchorPosition)

  return (
    <Popover
      sx={{
        zIndex: theme.zIndex.drawer - 1,
      }}
      open={open}
      onClose={onClose}
      anchorReference={useAnchorPosition ? 'anchorPosition' : 'anchorEl'}
      anchorPosition={anchorPosition}
      anchorEl={useAnchorPosition ? undefined : anchorEl}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: 'calc(100vh - 100px)',
            maxWidth: 420,
          },
        },
      }}
      disableScrollLock>
      <Card>
        <CardHeader
          avatar={!!onBack && <BackButton />}
          sx={cardActionStyle}
          action={!!onBack && <DismissButton />}
        />
        <CardContent>
          {content}
          {!!helpUrl && <SupportButton helpUrl={helpUrl} routeElementId={routeElementId} />}
        </CardContent>
      </Card>
    </Popover>
  )
}

export default OnboardingGuideDialog
